import {IncomingMessage} from "http";
import url from "url";

import {NextPage} from "next";
import {useRouter} from "next/router";
import {useTranslation} from "ni18n";
import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import Notice from "src/components/v5/Maps/Notice";
import OtherProviders from "src/components/v5/OtherProviders";
import useLocationConstants, {isFullPageBanner} from "src/constants/LocationConstants";
import {isAlaskaAirlinesLoc, isPopupLoc, isSaasLocation} from "src/constants/locations";
import {RootStateApptReason, RootStateLocation, RootStatePracticeSpecialty} from "src/store/types";
import {v4} from "uuid";

import {api} from "../../_services/api";
import {FeatureFlag} from "../../_services/featureFlagConstants";
import {fetchFeatureFlags} from "../../_services/featureFlags";
import {forwardTo, v5Pages} from "../../components/_common/_constants";
import {ands} from "../../components/_common/Carbon";
import Layout from "../../components/v5/Layout";
import Main from "../../components/v5/Location/Main";
import Photos from "../../components/v5/Location/Photos";
import TestimonialSlider from "../../components/v5/TestimonialSlider";
import {CareSlug} from "../../constants/cares";
import {SpecialtyId} from "../../constants/specialtyIds";
import {actions, useTypedSelector} from "../../store";
import {selectSelectedRegion} from "../../store/slices/userLocation";
import {NextPageContextWithStore} from "../../store/types";
import {s3ImageSource} from "../../useS3ImgSrc";
import {fetchLocalLocations} from "../../utils/fetchLocationsAndPractices";
import {ignoreArrays} from "../../utils/ignoreArrays";
import {removeFalsyKeys} from "../../utils/removeFalsyKeys";
import {getLocationSchemas} from "../../utils/schemaUtils";
import {startBookingFlow} from "../booking/utils/bookingQuery";

type Metadata = {
  title?: string;
  description?: string;
  h1?: string;
};

type Props = {
  location: RootStateLocation;
  flowId: string;
  popularReasons: RootStateApptReason[] | [];
  isTravelClearance?: boolean;
  metadata: Metadata;
};

// @ts-expect-error TS2322: Type '{ ({ flowId, location: _location, popularReasons, isTravelClearance, }: PropsWithChildren<Props>): Element; getInitialProps({ query, store, res, req, asPath, }: NextPageContextWithStore): Promise<{ location: RootStateLocation | undefined; popularReasons: any; isTravelClearance: boolean | undefined; flowId: string; }...' is not assignable to type 'NextPage<Props, Props>'.
const ClinicDetails: NextPage<Props> = ({location: _location, popularReasons, metadata}) => {
  const dispatch = useDispatch();
  const {query} = useRouter();
  const {slug} = query;

  const selectedRegion = useTypedSelector(selectSelectedRegion);

  const {locations = [], isMobile} = useTypedSelector(state => state.config);
  const location = _location || locations.find(l => l.slug === slug);

  const i18n = useTranslation();
  const isAlaska = isAlaskaAirlinesLoc(location.id);
  const {longitude, latitude} = location.address;

  const {images, name, specialties = {}} = location;

  const showImages = (images || []).length > 1;

  const isSaas = isSaasLocation(location);

  useEffect(() => {
    const selectedLocationsRegion = location.region.slug;
    if (selectedRegion !== selectedLocationsRegion) {
      dispatch(actions.setUserLocation({selectedRegion: selectedLocationsRegion}));
    }
  }, [location.region.slug, selectedRegion]);

  const pageTitle = specialties
    ? `${name || slug} | ${
        ands(
          i18n.lang,
          specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
        ) || ""
      }`
    : "Carbon Health";

  const pageDescription = specialties
    ? `${ands(
        i18n.lang,
        specialties.toArray<RootStatePracticeSpecialty>().map(s => s[1].name),
      )} | ${name}`
    : "Carbon Health";

  const ogImage = s3ImageSource(((images || [])[0] || {}).imageId, "jpg");

  // @ts-expect-error TS7006: Parameter 'd' implicitly has an 'any' type.
  const doctorFilter = d =>
    d.specialties.includes("Primary Care") && d.primaryLocationId === location.id;

  const bannerProps = useLocationConstants({locationId: location.id})?.banners.filter(b =>
    isFullPageBanner(b),
  )[0];

  const schemas = getLocationSchemas(location, ogImage);

  return (
    <Layout
      noIndex={isAlaska || isSaas}
      style={{backgroundColor: "#fff"}}
      pageTitle={metadata.title || pageTitle}
      pageDescription={metadata.description || pageDescription}
      className={!showImages ? "noImages" : ""}
      absolutePageImage={ogImage}
      navStartTransparent={false}
      schemas={schemas}
      blockChatWidget={isMobile}
      wrapClasses="ovf-unset"
      noHeader={isSaas}
      noFooter={isSaas}
    >
      {isSaas ? <></> : <div className="minh18 minh14-sm" />}
      {bannerProps && <Notice {...bannerProps} className="br0-f w100p-f jcc-f pt6-f pb4-f" />}
      {showImages && <Photos {...{location}} />}
      <Main
        h1={metadata.h1}
        isPopup={isPopupLoc(location.name)}
        showImages={(images || []).length > 1}
        location={location}
        popularReasons={popularReasons}
      />
      <OtherProviders
        filter={doctorFilter}
        tag={location.name}
        header={i18n.t("Meet our {{clinic}} providers.", {clinic: location.name})}
        coordinate={{longitude, latitude}}
        radius={0.1}
      />
      {isSaas ? <></> : <TestimonialSlider />}
    </Layout>
  );
};

const getLandingPageMetadata = async (slug: string | string[]): Promise<Metadata> => {
  try {
    const response = await api.getWithTimeout(15000, `/hib/landing-pages/location/?slug=${slug}`);
    const json = await response.json();
    return json || {};
  } catch {
    return {};
  }
};

async function shouldRedirectToBookingFlow(
  slug: string | null,
  utmSource: string | null,
  query: Record<string, string | string[] | undefined>,
  req?: IncomingMessage,
): Promise<boolean> {
  // Should not redirect when we have insufficient data
  if (!utmSource || !slug) {
    return false;
  }

  // Check FF for redirect
  const overrideAttribute = removeFalsyKeys({slug, utmSource});
  const {GROWTH_LOCATION_PAGE_GOOGLE_MAPS_REDIRECT_BOOKING} = await fetchFeatureFlags(
    {
      [FeatureFlag.GROWTH_LOCATION_PAGE_GOOGLE_MAPS_REDIRECT_BOOKING]: false,
    },
    {
      overrideAttribute,
      req,
      query,
    },
  );

  return GROWTH_LOCATION_PAGE_GOOGLE_MAPS_REDIRECT_BOOKING;
}

// @ts-expect-error TS2322: Type '({ query, store, res, req, asPath, }: NextPageContextWithStore) => Promise<{ location: RootStateLocation | undefined; popularReasons: any; isTravelClearance: boolean | undefined; flowId: string; }>' is not assignable to type '(context: NextPageContext<any>) => Props | Promise<Props>'.
ClinicDetails.getInitialProps = async ({
  query,
  store,
  res,
  asPath,
  req,
}: NextPageContextWithStore) => {
  const slug = ignoreArrays(query.slug);
  const utmSource = ignoreArrays(query.utm_source);
  const reduxLocations = store?.getState().config.locations;

  const [metadata, locations] = await Promise.all([
    slug ? getLandingPageMetadata(slug) : Promise.resolve(null),
    reduxLocations ? Promise.resolve(reduxLocations) : fetchLocalLocations(),
  ]);

  const location = locations.find(l => l.slug === slug);

  if (!location) {
    await forwardTo(res, v5Pages.locations);
    // For type narrowing
    return {};
  }

  const doBookingRedirect = await shouldRedirectToBookingFlow(slug, utmSource, query, req);
  if (doBookingRedirect) {
    const [bookingLink] = startBookingFlow({
      appointmentReasonSlug: CareSlug.GENERAL_SICK_VISIT,
      locationSlug: slug,
      originUrl: asPath,
      specialtyId: SpecialtyId.URGENT_CARE,
    });
    await forwardTo(res, url.format(bookingLink), 307);
  }

  let popularReasons = [];

  if (location) {
    const url = `/hib/locations/${location.id}/popularApptReasons`;
    popularReasons = await api.get(url);
  }

  return {location, popularReasons, flowId: v4(), metadata};
};

export default ClinicDetails;
